import React from "react";
import { useDispatch, useSelector } from "react-redux";
import CreatableSelect from "react-select/creatable";

import { setBookingFormFieldValueOnly } from "shared/data/actions/bookingForm";
import { AppState } from "shared/data/reducers";
import {
  PassportFieldUpdate,
  PassportSelectOption,
  getCbtTravelerPassportsForField,
  getPassportFieldUpdates,
  getPassportSelectOptions,
} from "shared/lib/bookingFormPassportSelector";
import { BookingFormInputProps } from "shared/types";

import FormErrorText from "@components/UI/Form/FormErrorText";
import SimpleSelect, { FormControl } from "@components/UI/SimpleSelect";

import BookingFormInput from "./BookingFormInput";

export default function BookingFormPassportSelect({
  value,
  onChange,
  isError,
  errorType,
  errorMessageOptions,
  isPasswordField,
  name,
  format,
  dateRange,
}: BookingFormInputProps) {
  const dispatch = useDispatch();
  const { cbt, bookingFormFields } = useSelector((state: AppState) => ({
    cbt: state.cbt,
    bookingFormFields: state.bookingForm.bookingFormFields,
  }));

  const passports = getCbtTravelerPassportsForField({ cbt, fieldName: name });
  const options = getPassportSelectOptions(passports);

  if (options.length === 0) {
    return (
      <BookingFormInput
        value={value}
        onChange={onChange}
        format={format}
        isError={isError}
        isPasswordField={isPasswordField}
        errorType={errorType}
        errorMessageOptions={errorMessageOptions}
        name={name}
        dateRange={dateRange}
      />
    );
  }

  const selectedValue: PassportSelectOption | null = value
    ? options.find((option) => option.value === value) || {
        label: value,
        value,
      }
    : null;

  const applyPassportUpdates = (updates: PassportFieldUpdate[]) => {
    updates.forEach((update) => {
      dispatch(setBookingFormFieldValueOnly(update.name, update.value));
    });
  };

  const handlePassportNumberChange = (
    newValue: PassportSelectOption | null
  ) => {
    const passport = newValue?.passport;
    const manualPassportNumber = newValue?.value || "";

    applyPassportUpdates(
      getPassportFieldUpdates({
        bookingFormFields,
        passportNumberFieldName: name,
        passport,
        manualPassportNumber: passport ? undefined : manualPassportNumber,
      })
    );
  };

  return (
    <>
      <SimpleSelect
        selectComponent={CreatableSelect}
        isClearable
        isSearchable
        formatCreateLabel={(inputValue) => inputValue}
        testId={`custom-select-${name}`}
        components={{ Control: FormControl }}
        controlOptions={{ value, error: isError }}
        styles={{
          container: (base) => ({
            ...base,
            flexGrow: 1,
          }),
        }}
        options={options}
        value={selectedValue}
        onChange={handlePassportNumberChange}
      />
      {isError && (
        <div className="form-text-input-error">
          <FormErrorText
            error={errorType}
            errorMessageOptions={errorMessageOptions}
          />
        </div>
      )}
    </>
  );
}
